/**
 * leafFields.tsx — shared leaf-field vocabulary + value editor for the Talent Pool query tree.
 *
 * One source of truth for "given a Filter, which control edits its value" — reused by the recursive
 * FilterModal (AST editor) and the inline chip popover (TreeFilters). Keeps the two in lockstep so a
 * field always edits the same way wherever it appears.
 */

import { AutocompleteInput, Similarity, Segmented, FilterSelect, RangeRow, ResolveInput } from './controls';
import { SENIORITY_OPTS, STUDY_LEVEL_OPTS, IS_MANAGER_OPTS, JOB_HOPPER_OPTS, PROFICIENCY_OPTS, COMPANY_SIZE_OPTS, DEPARTMENT_OPTS, type Opt } from './tpEnums';
import type { Filter } from '@/lib/talentPool.api';

// value fields carry a similarity dial; ranges are numeric; the rest are enums/entities.
export const SIM_FIELDS = new Set(['title', 'company', 'industry', 'skill', 'field_of_study', 'degree', 'certification', 'responsibilities', 'summary', 'name']);
export const RANGE_FIELDS = new Set(['experience_years', 'avg_tenure', 'current_role', 'num_jobs', 'grad_year', 'salary', 'notice_period']);
// fields whose meaning depends on WHEN in the career it held (current vs. past role).
export const EXPERIENCE_WHEN = new Set(['title', 'company', 'industry', 'department', 'responsibilities', 'seniority', 'is_manager', 'skill']);
export const RANGE_UNIT: Record<string, string> = { experience_years: 'yrs', avg_tenure: 'yrs', current_role: 'yrs', num_jobs: 'jobs', grad_year: '', salary: '$', notice_period: 'days' };
// title defaults LOW (0.1 = lexical word-match, no semantic leg) — precision-first, matching the planner;
// the recruiter raises the slider to widen semantically.
const DEFAULT_SIM: Record<string, number> = { title: 0.1, company: 0.15, skill: 0.4, industry: 0.5, field_of_study: 0.55, degree: 0.4, university: 0.45, certification: 0.2, responsibilities: 0.6, summary: 0.6, name: 0.3 };

// the leaf-field picker, grouped by section (rendered flat with section headers in the dropdown)
export const FIELD_GROUPS: { label: string; options: Opt[] }[] = [
    { label: 'Role', options: [
        { value: 'title', label: 'Job title' },
        { value: 'seniority', label: 'Seniority level' },
        { value: 'is_manager', label: 'Manager / IC' },
        { value: 'department', label: 'Department' },
        { value: 'responsibilities', label: 'Responsibilities' },
    ] },
    { label: 'Company', options: [
        { value: 'company', label: 'Company' },
        { value: 'company_size', label: 'Company size' },
        { value: 'industry', label: 'Industry' },
    ] },
    { label: 'Location', options: [
        { value: 'location_city', label: 'City' },
        { value: 'location_country', label: 'Country / region' },
    ] },
    { label: 'Skills', options: [
        { value: 'skill', label: 'Skill' },
        { value: 'certification', label: 'Certification' },
    ] },
    { label: 'Education', options: [
        { value: 'field_of_study', label: 'Field of study' },
        { value: 'degree', label: 'Degree' },
        { value: 'university', label: 'University' },
        { value: 'study_level', label: 'Study level' },
        { value: 'education_country', label: 'Studied in' },
        { value: 'grad_year', label: 'Graduation year' },
    ] },
    { label: 'Languages', options: [{ value: 'language', label: 'Language' }] },
    { label: 'Experience', options: [
        { value: 'experience_years', label: 'Years of experience' },
        { value: 'avg_tenure', label: 'Average tenure' },
        { value: 'current_role', label: 'Time in current role' },
        { value: 'num_jobs', label: 'Number of roles' },
        { value: 'job_hopper', label: 'Job hopper' },
    ] },
    { label: 'Other', options: [
        { value: 'salary', label: 'Expected salary' },
        { value: 'notice_period', label: 'Notice period' },
        { value: 'work_authorized_country', label: 'Work authorized in' },
        { value: 'needs_visa_country', label: 'Needs visa for' },
    ] },
];
export const ALL_FIELD_OPTS: Opt[] = FIELD_GROUPS.flatMap((g) => g.options);
export const fieldLabel = (field: string) => ALL_FIELD_OPTS.find((o) => o.value === field)?.label ?? field;

// enum fields need a value to be meaningful — seed a sensible default so a freshly-added leaf counts.
const ENUM_DEFAULT: Record<string, string[]> = { is_manager: ['1'], job_hopper: ['1'], seniority: ['senior'], study_level: ['bachelor'] };

/** A fresh leaf for a field, with a sensible default value + the field's default similarity where relevant. */
export function newLeaf(field = 'skill'): Filter {
    return { field, values: ENUM_DEFAULT[field] ? [...ENUM_DEFAULT[field]] : [], ...(SIM_FIELDS.has(field) ? { similarity: DEFAULT_SIM[field] ?? 0.4 } : {}) };
}

/** The field-appropriate value control(s) for a leaf — chips+similarity, an enum, a range, or an entity typeahead. */
export function LeafValueEditor({ f, onChange }: { f: Filter; onChange: (next: Filter) => void }) {
    const vals = f.values ?? [];
    const setVals = (values: string[]) => onChange({ ...f, values });

    if (RANGE_FIELDS.has(f.field)) {
        return <RangeRow value={{ gte: f.gte, lte: f.lte }} onChange={(r) => onChange({ ...f, gte: r.gte, lte: r.lte })} unit={RANGE_UNIT[f.field] ?? ''} />;
    }
    if (f.field === 'is_manager' || f.field === 'job_hopper') {
        return <Segmented value={vals[0] ?? '1'} options={(f.field === 'is_manager' ? IS_MANAGER_OPTS : JOB_HOPPER_OPTS).filter((o) => o.value !== 'any')} onChange={(v) => setVals([v])} />;
    }
    if (f.field === 'seniority' || f.field === 'study_level') {
        return <FilterSelect value={vals[0] ?? ''} options={f.field === 'seniority' ? SENIORITY_OPTS : STUDY_LEVEL_OPTS} onChange={(v) => setVals([v as string])} />;
    }
    if (f.field === 'company_size') {
        return <FilterSelect multiple value={vals} options={COMPANY_SIZE_OPTS} placeholder="Any size" onChange={(v) => setVals(v as string[])} />;
    }
    if (f.field === 'department') {
        return <FilterSelect multiple value={vals} options={DEPARTMENT_OPTS} placeholder="Any department" onChange={(v) => setVals(v as string[])} />;
    }
    if (f.field === 'location_city' || f.field === 'university') {
        return <ResolveInput field={f.field} placeholder={f.field === 'university' ? 'Stanford, NUS…' : 'Bengaluru, San Francisco…'} values={vals} onChange={setVals} />;
    }
    // default: value chips (+ similarity for value fields, + proficiency floor for languages)
    return (
        <>
            <AutocompleteInput
                placeholder="Add value, Enter…"
                values={vals.map((v) => ({ label: v }))}
                onAdd={(t) => setVals([...vals, t])}
                onRemove={(i) => setVals(vals.filter((_, x) => x !== i))}
            />
            {f.field === 'language' && (
                <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-zinc-500">Min proficiency</span>
                    <FilterSelect minWidth={150} value={f.proficiency || 'any'} options={PROFICIENCY_OPTS} onChange={(v) => onChange({ ...f, proficiency: v === 'any' ? undefined : (v as string) })} />
                </div>
            )}
            {SIM_FIELDS.has(f.field) && <Similarity value={f.similarity ?? DEFAULT_SIM[f.field] ?? 0.4} onChange={(v) => onChange({ ...f, similarity: v })} />}
        </>
    );
}
